import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../components/ui/Button.js'
import { Input } from '../components/ui/Input.js'
import { ItemSelector, type ItemSelectorRef } from '../components/orders/ItemSelector.js'
import { ProjectSelector } from '../components/orders/ProjectSelector.js'
import { OrderItemRow } from '../components/orders/OrderItemRow.js'
import type { OrderItem, CatalogItem } from '../types/orders.js'
import type { CreateOrderRequest, UpdateOrderRequest } from '../contracts/orders.js'
import type { Project } from '../data/projects.js'
import { formatCurrency, calculateItemTotal } from '../lib/orders-utils.js'
import { getCurrentDate, calculateDeliveryEstimate } from '../lib/date-utils.js'
import { createApiClient } from '../lib/api.js'
import { useThemeTokens } from '../themes/index.js'
import { getProjectById } from '../data/projects.js'
import { products } from '../data/essco_products.json'
import { useKeyboardNavigation } from '../hooks/keyboard/index.js'

// Use contract types directly
interface FormData {
  title: string
  project_id?: number
  job_number: string
  delivery_date: string
  created_at: string
  notes: string
  items: OrderItem[]
}

// Contract-first approach: project_id is always a number

export const CreateOrder: React.FC = () => {
  const navigate = useNavigate()
  const { orderId } = useParams<{ orderId: string }>()
  const isEditing = Boolean(orderId)
  const theme = useThemeTokens()
  
  // Refs for focus management
  const itemSelectorRef = useRef<ItemSelectorRef>(null)
  const quantityInputRefs = useRef<{[key: string]: HTMLInputElement | null}>({})
  const [lastAddedItemId, setLastAddedItemId] = useState<string | null>(null)
  
  const [formData, setFormData] = useState<FormData>({
    title: '',
    job_number: '',
    delivery_date: '',
    created_at: new Date().toISOString().split('T')[0], // Default to today
    notes: '',
    items: []
  })
  
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Keyboard navigation for quantity inputs
  const { handleKeyDown: handleQuantityKeyDown } = useKeyboardNavigation({
    Enter: () => {
      // When Enter is pressed in quantity input, clear search and return focus
      itemSelectorRef.current?.clearSearchAndFocus()
      setLastAddedItemId(null)
    },
    Escape: () => {
      // Escape also returns to search
      itemSelectorRef.current?.clearSearchAndFocus()
      setLastAddedItemId(null)
    }
  })

  // Load order data if editing
  useEffect(() => {
    if (isEditing && orderId) {
      loadOrderData(orderId)
    }
  }, [isEditing, orderId])

  // Focus on quantity input when new item is added
  useEffect(() => {
    if (lastAddedItemId && quantityInputRefs.current[lastAddedItemId]) {
      const input = quantityInputRefs.current[lastAddedItemId]
      input?.focus()
      input?.select() // Select all text so user can just type the new quantity
    }
  }, [lastAddedItemId, formData.items])

  const loadOrderData = async (id: string) => {
    try {
      setLoading(true)
      const apiClient = await createApiClient()
      const token = localStorage.getItem('token') || 'dev-token-1'
      const orders = await apiClient.getOrdersEnhanced(token)
      const order = orders.find(o => o.id === parseInt(id))
      
      if (!order) {
        throw new Error('Order not found')
      }
      
      // Map OrderResponse fields directly to FormData (contract-compliant)
      setFormData({
        title: order.title,
        project_id: order.project_id || undefined,
        job_number: order.job_number || '',
        delivery_date: order.delivery_date || '',
        created_at: order.created_at.split('T')[0], // Convert ISO date to YYYY-MM-DD format
        notes: order.notes || '',
        items: order.items ? order.items.map(item => ({
          id: item.id.toString(),
          catalogItem: {
            id: item.product_id,
            name: item.product_name,
            pricePerUnit: item.unit_price,
            description: '',
            category: '',
            unit: 'each',
            qtyPerUnit: 1,
            perItem: item.unit_price,
            orders: 0,
            source: 'api'
          },
          quantity: item.quantity,
          totalPrice: item.total_price
        })) : []
      })

      // Load project data if available
      if (order.project_id) {
        // Contract-compliant: project_id is always number
        const project = getProjectById(order.project_id)
        if (project) {
          setSelectedProject(project)
        }
      }
    } catch (err) {
      setError('Failed to load order data')
      console.error('Error loading order:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project)
    setFormData(prev => ({
      ...prev,
      title: project.name,
      project_id: project.id, // Contract-compliant: project.id is always number
      job_number: project.jobNumber // Auto-populate job number from project
    }))
  }

  const handleAddItem = (catalogItem: CatalogItem, quantity: number) => {
    const newItem: OrderItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      catalogItem,
      quantity,
      totalPrice: calculateItemTotal(quantity, catalogItem.pricePerUnit)
    }
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }))
    
    // Set this item to receive focus
    setLastAddedItemId(newItem.id)
  }

  const handleItemAdded = () => {
    // Called when ItemSelector adds an item - this triggers focus management
    // The useEffect above will handle the actual focusing
  }

  const handleRemoveItem = (itemId: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }))
    
    // Clean up the ref
    delete quantityInputRefs.current[itemId]
    
    // Clear focus target if this was the focused item
    if (lastAddedItemId === itemId) {
      setLastAddedItemId(null)
    }
  }

  const handleUpdateItemQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(itemId)
      return
    }

    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            quantity,
            totalPrice: calculateItemTotal(quantity, item.catalogItem.pricePerUnit)
          }
        }
        return item
      })
    }))
  }

  const calculateTotalAmount = () => {
    return formData.items.reduce((total, item) => total + item.totalPrice, 0)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.title.trim()) {
      setError('Project name is required')
      return
    }
    
    if (!formData.job_number.trim()) {
      setError('Job number is required (automatically set by project)')
      return
    }
    
    if (!formData.notes.trim()) {
      setError('Notes field is required (include PO information)')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const apiClient = await createApiClient()
      const token = localStorage.getItem('token') || 'dev-token-1'
      
      // Helper function to find product from catalog
      const findProduct = (catalogItemId: string) => {
        return products.find(p => p.id === catalogItemId)
      }

      if (isEditing && orderId) {
        // Update existing order - use contract types directly
        const updateRequest: UpdateOrderRequest = {
          title: formData.title,
          project_id: formData.project_id,
          job_number: formData.job_number,
          delivery_date: formData.delivery_date || undefined,
          notes: formData.notes,
          items: formData.items.map(item => {
            const product = findProduct(item.catalogItem.id)
            const unitPrice = product?.price || 0
            return {
              product_id: item.catalogItem.id,
              product_name: item.catalogItem.name,
              quantity: item.quantity,
              unit: item.catalogItem.unit, // Include unit from catalog
              unit_price: unitPrice,
              total_price: item.quantity * unitPrice
            }
          })
        }
        await apiClient.updateOrderEnhanced(token, orderId, updateRequest)
      } else {
        // Create new order - use contract types directly
        const createRequest: CreateOrderRequest = {
          title: formData.title,
          project_id: formData.project_id,
          job_number: formData.job_number,
          delivery_date: formData.delivery_date || undefined,
          created_at: formData.created_at ? new Date(formData.created_at).toISOString() : undefined,
          notes: formData.notes,
          items: formData.items.map(item => {
            const product = findProduct(item.catalogItem.id)
            const unitPrice = product?.price || 0
            return {
              product_id: item.catalogItem.id,
              product_name: item.catalogItem.name,
              quantity: item.quantity,
              unit: item.catalogItem.unit, // Include unit from catalog
              unit_price: unitPrice,
              total_price: item.quantity * unitPrice
            }
          })
        }
        await apiClient.createOrderEnhanced(token, createRequest)
      }
      
      // Navigate back to orders page
      navigate('/orders')
    } catch (err) {
      console.error('Failed to save order:', err)
      setError('Failed to save order. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/orders')
  }

  // Calculate delivery estimate based on the selected creation date
  const deliveryEstimate = calculateDeliveryEstimate(
    formData.created_at ? new Date(formData.created_at).toISOString() : new Date().toISOString()
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner">Loading order...</div>
      </div>
    )
  }

  return (
    <div 
      className="create-order-page min-h-screen"
      style={{ 
        padding: theme.spacing.xl,
        fontFamily: theme.typography.fontFamily,
        color: theme.colors.text,
        backgroundColor: theme.colors.background
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">
            {isEditing ? 'Edit Order' : 'Create New Order'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEditing ? 'Update order details and items' : 'Create a new environmental project order'}
          </p>
        </div>
        <Button 
          variant="ghost" 
          onClick={handleCancel}
          className="text-gray-600 hover:text-gray-800"
        >
          ← Back to Orders
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="max-w-6xl">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {/* Basic Info Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Project Information</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-2">
              <ProjectSelector
                selectedProject={selectedProject}
                onProjectSelect={handleProjectSelect}
                placeholder="Search for a project..."
                label="Project Name/Location *"
                required
              />
            </div>
            
            <Input
              label="Notes (PO Information) *"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="PO: PO-2025-001"
              required
            />
          </div>
        </div>

        {/* Dates Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Timeline</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Input
              label="Date Created"
              type="date"
              value={formData.created_at}
              onChange={(e) => handleInputChange('created_at', e.target.value)}
            />
            
            <Input
              label="Delivery Estimate"
              value={deliveryEstimate}
              disabled
              readOnly
            />
            
            {/* Delivery Date - Optional with Add Button */}
            <div>
              {formData.delivery_date ? (
                <div className="space-y-2">
                  <Input
                    label="Delivery Date"
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => handleInputChange('delivery_date', e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleInputChange('delivery_date', '')}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Remove Date
                  </Button>
                </div>
              ) : (
                <div className="pt-6">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleInputChange('delivery_date', getCurrentDate())}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <span className="text-lg">+</span>
                    Add Delivery Date
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Items Section - Constrained to reasonable width on large screens */}
        <div style={{ width: '100%', maxWidth: '800px' }}>
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Order Items</h2>
              <div className="text-2xl font-bold text-green-600">
                Total: {formatCurrency(calculateTotalAmount())}
              </div>
            </div>

            {/* Add Item Selector */}
            <div className="mb-6">
              <ItemSelector
                ref={itemSelectorRef}
                onItemSelect={handleAddItem}
                onItemAdded={handleItemAdded}
              />
            </div>

          {/* Items List */}
          {formData.items.length > 0 ? (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              {/* Table Header */}
              <div style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '60px 70px 1fr 100px 120px 100px 60px',
                  gap: '16px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  <div style={{ textAlign: 'center' }}>#</div>
                  <div style={{ textAlign: 'center' }}>Image</div>
                  <div>Item</div>
                  <div style={{ textAlign: 'center' }}>Unit</div>
                  <div style={{ textAlign: 'right' }}>Price/Unit</div>
                  <div style={{ textAlign: 'right' }}>Total</div>
                  <div style={{ textAlign: 'center' }}>×</div>
                </div>
              </div>
              
              {/* Table Body */}
              {formData.items.map((item) => (
                <OrderItemRow
                  key={item.id}
                  item={item}
                  mode="editable"
                  onQuantityChange={handleUpdateItemQuantity}
                  onRemoveItem={handleRemoveItem}
                  setQuantityInputRef={(itemId, ref) => { quantityInputRefs.current[itemId] = ref }}
                  onKeyDown={handleQuantityKeyDown}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
              <div className="text-lg mb-2">No items added yet</div>
              <div className="text-sm">Use the selector above to add items to your order</div>
            </div>
          )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 pt-6">
          <Button type="button" variant="ghost" onClick={handleCancel} size="lg">
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading || formData.items.length === 0}
            size="lg"
          >
            {loading ? 'Saving...' : isEditing ? 'Update Order' : 'Create Order'}
          </Button>
        </div>
        
        {/* Bottom spacing for scroll visibility */}
        <div style={{ height: '400px' }}></div>
      </form>
    </div>
  )
} 